import React from "react";
import { Collapse, Stack, Box, useTheme } from "@mui/material";
import LeadProfile from "@/modules/lead-page/new/components/sidebar/lead-profile";
import CustomerProfileSkeleton from "@/modules/lead-page/new/components/sidebar/skeletons/customer-profile-skeleton";
import LeadSidebar from "@/modules/lead-page/new/components/sidebar/lead-sidebar";
import LeadHeaderSkeleton from "@/modules/lead-page/new/components/header/lead-header-skeleton";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import MobileLeadHeader from "./mobile-lead-header";
import MobileBottomSheets from "./mobile-bottom-sheets";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { ActivityProvider } from "@/modules/lead-page/new/context/activity.context";
import { ActivityScrollProvider } from "@/modules/lead-page/new/context/activity-scroll-context";

interface MobileLayoutProps {
  isLoading: boolean;
  isHeaderCollapsed: boolean;
  isLeadArchived: boolean;
  onPersonClick: () => void;
  // Sidebar modal
  sidebarModalOpen: boolean;
  onSidebarModalClose: () => void;
  // Bottom sheet states
  isTasksOpen: boolean;
  isNextActionsOpen: boolean;
  isRecommendationsOpen: boolean;
  isActivitiesOpen: boolean;
  isSalesOpen: boolean;
  isPropertiesOpen: boolean;
  // Bottom sheet handlers
  onTasksClick: () => void;
  onNextActionsClick: () => void;
  onRecommendationsClick: () => void;
  onActivitiesClick: () => void;
  onSalesClick: () => void;
  onPropertiesClick: () => void;
  onTasksClose: () => void;
  onNextActionsClose: () => void;
  onRecommendationsClose: () => void;
  onActivitiesClose: () => void;
  onSalesClose: () => void;
  onPropertiesClose: () => void;
  // Interests
  isInterestsOpen: boolean;
  onInterestsClick: () => void;
  onInterestsClose: () => void;
  // Developer Tracks
  isDeveloperTracksOpen: boolean;
  onDeveloperTracksClick: () => void;
  onDeveloperTracksClose: () => void;
  // reviews
  onRatingsClick: () => void;
  onRatingsClose: () => void;
  isRatingsVisible: boolean;
  isRatingsOpen: boolean;
  // Developer Tracks visibility
  isDeveloperTracksVisible: boolean;
}

const MobileLayout: React.FC<MobileLayoutProps> = ({
  isLoading,
  isHeaderCollapsed,
  isLeadArchived,
  onPersonClick,
  sidebarModalOpen,
  onSidebarModalClose,
  isTasksOpen,
  isNextActionsOpen,
  isRecommendationsOpen,
  isActivitiesOpen,
  isSalesOpen,
  isPropertiesOpen,
  onTasksClick,
  onNextActionsClick,
  onRecommendationsClick,
  onActivitiesClick,
  onSalesClick,
  onPropertiesClick,
  onTasksClose,
  onNextActionsClose,
  onRecommendationsClose,
  onActivitiesClose,
  onSalesClose,
  onPropertiesClose,
  isInterestsOpen,
  onInterestsClick,
  onInterestsClose,
  isDeveloperTracksOpen,
  onDeveloperTracksClick,
  onDeveloperTracksClose,
  onRatingsClick,
  onRatingsClose,
  isRatingsVisible,
  isRatingsOpen,
  isDeveloperTracksVisible,
}) => {
  const theme = useTheme();

  return (
    <Stack direction="column" flex={1} width="100%">
      {/* Mobile: Compact profile at top */}
      <Stack
        direction="column"
        sx={{
          width: "100%",
          padding: theme.nawy.spacing.md,
          borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {isLoading ? (
          <CustomerProfileSkeleton />
        ) : (
          <ArchivedOverlay isArchived={isLeadArchived}>
            <LeadProfile
              onPersonClick={onPersonClick}
              useCompactLayout={true}
            />
          </ArchivedOverlay>
        )}
      </Stack>

      {/* Lead Information with Action Cards */}
      {isLoading ? (
        <LeadHeaderSkeleton />
      ) : (
        <Collapse
          in={!isHeaderCollapsed}
          timeout={600}
          collapsedSize={0}
          sx={{
            "& .MuiCollapse-wrapperInner": {
              width: "100%",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
              transform: isHeaderCollapsed
                ? "translateY(-10px)"
                : "translateY(0)",
              opacity: isHeaderCollapsed ? 0 : 1,
            }}
          >
            <MobileLeadHeader
              isLeadArchived={isLeadArchived}
              onTasksClick={onTasksClick}
              onNextActionsClick={onNextActionsClick}
              onRecommendationsClick={onRecommendationsClick}
              onActivitiesClick={onActivitiesClick}
              onSalesClick={onSalesClick}
              onPropertiesClick={onPropertiesClick}
              onInterestsClick={onInterestsClick}
              onDeveloperTracksClick={onDeveloperTracksClick}
              onRatingsClick={onRatingsClick}
              isRatingsOpen={isRatingsVisible}
              isDeveloperTracksVisible={isDeveloperTracksVisible}
            />
          </Box>
        </Collapse>
      )}

      {/* Bottom Sheets - Action Cards */}
      <ActivityProvider>
        <ActivityScrollProvider>
          <MobileBottomSheets
            isTasksOpen={isTasksOpen}
            isNextActionsOpen={isNextActionsOpen}
            isRecommendationsOpen={isRecommendationsOpen}
            isActivitiesOpen={isActivitiesOpen}
            isSalesOpen={isSalesOpen}
            isPropertiesOpen={isPropertiesOpen}
            onTasksClose={onTasksClose}
            onNextActionsClose={onNextActionsClose}
            onRecommendationsClose={onRecommendationsClose}
            onActivitiesClose={onActivitiesClose}
            onSalesClose={onSalesClose}
            onPropertiesClose={onPropertiesClose}
            isInterestsOpen={isInterestsOpen}
            onInterestsClose={onInterestsClose}
            isDeveloperTracksOpen={isDeveloperTracksOpen}
            onDeveloperTracksClose={onDeveloperTracksClose}
            isRatingsOpen={isRatingsOpen}
            onRatingsClose={onRatingsClose}
            onActivitiesClick={onActivitiesClick}
            onDeveloperTracksClick={onDeveloperTracksClick}
          />
        </ActivityScrollProvider>
      </ActivityProvider>
      {/* Bottom Sheet - Sidebar Modal */}
      <CommonBottomSheet
        isOpen={sidebarModalOpen}
        onClose={onSidebarModalClose}
        snapPoints={[1]}
      >
        <LeadSidebar />
      </CommonBottomSheet>
    </Stack>
  );
};

export default MobileLayout;
